import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Flag, CheckCircle } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchBikes, submitTestRide } from "@/lib/api";

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  bike_id: "",
  license_years: "",
  notes: "",
};

export default function TestRide() {
  const { bikeId } = useParams();
  const [bikes, setBikes] = useState([]);
  const [form, setForm] = useState({ ...initialForm, bike_id: bikeId || "" });
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchBikes().then(setBikes).catch(() => setBikes([]));
  }, []);

  useEffect(() => {
    if (bikeId) setForm((f) => ({ ...f, bike_id: bikeId }));
  }, [bikeId]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const selectedBike = bikes.find((b) => b.id === form.bike_id);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.bike_id) { toast.error("Επίλεξε μηχανή"); return; }
    if (!date) { toast.error("Διάλεξε ημερομηνία"); return; }
    if (!form.full_name || !form.email || !form.phone || !form.license_years) {
      toast.error("Συμπλήρωσε όλα τα υποχρεωτικά πεδία");
      return;
    }
    setSubmitting(true);
    try {
      await submitTestRide({
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        bike_id: form.bike_id,
        bike_label: selectedBike ? `${selectedBike.brand} ${selectedBike.model}` : "Επιλεγμένη μηχανή",
        preferred_date: format(date, "yyyy-MM-dd"),
        license_years: Number(form.license_years),
        notes: form.notes,
      });
      toast.success("Το αίτημα για δοκιμή παραλήφθηκε. Σε περιμένουμε στη σέλα.");
      setSuccess(true);
      setForm(initialForm);
      setDate(undefined);
    } catch (err) {
      toast.error("Αποτυχία υποβολής. Δοκίμασε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="test-ride-page" className="pt-40 md:pt-52 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-overline mb-4">Σελλώσου</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl leading-[0.9]">
            Κλείσε<br/>
            <span className="text-[#E50000]">δοκιμή.</span>
          </h1>
          <p className="mt-6 text-white/65 leading-relaxed">
            Καβαλίκεψε τη μηχανή πριν δεσμευτείς. Φέρε δίπλωμα και ταυτότητα — θα ετοιμάσουμε τη μηχανή και θα σε περιμένουμε στο showroom.
          </p>

          {selectedBike && (
            <div data-testid="selected-bike-preview" className="mt-10 border border-white/10 overflow-hidden">
              <img src={selectedBike.image_url} alt={selectedBike.model} className="w-full h-48 object-cover" />
              <div className="p-5 bg-[#141414]">
                <p className="text-overline">{selectedBike.brand} · {selectedBike.year}</p>
                <p className="font-display font-black uppercase text-2xl mt-1">{selectedBike.model}</p>
                <p className="font-mono text-xs text-white/50 mt-2">{selectedBike.engine_cc}cc · {selectedBike.power_hp} hp</p>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div data-testid="test-ride-success" className="border border-white/10 bg-[#141414] p-10 text-center">
              <CheckCircle size={48} weight="duotone" className="text-[#E50000] mx-auto" />
              <h2 className="font-display font-black uppercase text-3xl mt-6">Το αίτημα παραλήφθηκε</h2>
              <p className="mt-3 text-white/60">Θα επικοινωνήσουμε εντός μιας ημέρας για να κλειδώσουμε τη δοκιμή σου.</p>
              <button data-testid="test-ride-new-btn" onClick={() => setSuccess(false)} className="btn-outline text-xs mt-8">
                Νέο αίτημα
              </button>
            </div>
          ) : (
            <form
              data-testid="test-ride-form"
              onSubmit={onSubmit}
              className="border border-white/10 bg-[#141414] p-8 md:p-10 space-y-6"
            >
              <Field label="Επίλεξε Μηχανή" required>
                <Select value={form.bike_id} onValueChange={(v) => setForm(f => ({...f, bike_id: v}))}>
                  <SelectTrigger data-testid="test-ride-bike-trigger" className="form-input h-auto py-3 justify-between">
                    <SelectValue placeholder="Επίλεξε μηχανή" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border border-white/15 text-white rounded-none max-h-72">
                    {bikes.map(b => (
                      <SelectItem key={b.id} value={b.id} className="focus:bg-[#E50000] focus:text-white">
                        {b.brand} {b.model} · {b.year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Ονοματεπώνυμο" required>
                  <input data-testid="ride-name-input" required type="text" value={form.full_name} onChange={update("full_name")} className="form-input" />
                </Field>
                <Field label="Email" required>
                  <input data-testid="ride-email-input" required type="email" value={form.email} onChange={update("email")} className="form-input" />
                </Field>
                <Field label="Τηλέφωνο" required>
                  <input data-testid="ride-phone-input" required type="tel" value={form.phone} onChange={update("phone")} className="form-input" />
                </Field>
                <Field label="Χρόνια με Δίπλωμα" required>
                  <input data-testid="ride-license-input" required type="number" min="0" max="60" value={form.license_years} onChange={update("license_years")} className="form-input" placeholder="π.χ. 5" />
                </Field>
              </div>

              <Field label="Επιθυμητή Ημερομηνία" required>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="ride-date-trigger"
                      className="form-input flex items-center justify-between text-left"
                    >
                      <span className={date ? "text-white" : "text-white/40"}>
                        {date ? format(date, "PPP", { locale: el }) : "Επίλεξε ημερομηνία"}
                      </span>
                      <CalendarIcon size={18} className="text-[#E50000]" weight="bold" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#141414] border-white/15 rounded-none" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                      locale={el}
                    />
                  </PopoverContent>
                </Popover>
              </Field>

              <Field label="Σημειώσεις">
                <textarea data-testid="ride-notes-input" rows={4} value={form.notes} onChange={update("notes")} className="form-input resize-none" placeholder="Κάτι που να ξέρουμε; Εμπειρία, προτιμώμενη διαδρομή..." />
              </Field>

              <button
                data-testid="ride-submit-btn"
                disabled={submitting}
                type="submit"
                className="btn-primary text-xs w-full md:w-auto disabled:opacity-50"
              >
                <Flag size={14} weight="bold" className="mr-2" />
                {submitting ? "Υποβολή..." : "Ζήτα Δοκιμή"}
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #1A1A1A;
          border: 1px solid rgba(255,255,255,0.15);
          color: white;
          padding: 0.85rem 1rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s;
          border-radius: 2px;
        }
        .form-input:focus { border-color: #E50000; }
        .form-input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">
        {label}{required && <span className="text-[#E50000]"> *</span>}
      </span>
      {children}
    </label>
  );
}
